import React, { useState, useEffect } from 'react';
import { ppeApi } from '../services/ppeApi';
import { projectApi, Project as BackendProject } from '../services/projectApi';
import { employeeApi } from '../services/employeeApi';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import NewProjectModal from '../components/NewProjectModal';

interface Employee {
  id: number;
  name: string;
  department: string;
  position: string;
}

interface ScheduleEvent {
  id: string | number;
  title: string;
  start: string;
  end: string;
  type: 'MEETING' | 'PROJECT' | 'LEAVE' | 'TRAINING' | 'OTHER' | 'PAYMENT';
  paymentType?: string; // 입금 유형 (데이터에서 동적으로)
  employeeId: number;
  employeeName: string;
  description?: string;
  location?: string;
  amount?: number; // 입금 금액 (만원)
}

const ScheduleAdmin: React.FC = () => {
  const { userRole } = useAuth();
  const { showToast } = useAlert();
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // PPE 상세 모달 관련 상태
  const [isPPEDetailOpen, setIsPPEDetailOpen] = useState(false);
  const [selectedPPEProject, setSelectedPPEProject] = useState<BackendProject | undefined>();
  const [allProjects, setAllProjects] = useState<BackendProject[]>([]);

  // 필터 상태: 프로젝트 + 동적 입금 유형들
  const [showProjects, setShowProjects] = useState(true);
  const [paymentTypes, setPaymentTypes] = useState<string[]>([]); // 데이터에서 추출한 유형들
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<Set<string>>(new Set());

  // 툴팁 상태
  const [tooltipInfo, setTooltipInfo] = useState<{text: string; x: number; y: number} | null>(null);

  // 프로젝트별 색상 생성
  const getProjectColor = (projectId: number) => {
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200', 
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-yellow-100 text-yellow-800 border-yellow-200',
      'bg-red-100 text-red-800 border-red-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-teal-100 text-teal-800 border-teal-200',
      'bg-cyan-100 text-cyan-800 border-cyan-200'
    ];
    return colors[projectId % colors.length];
  };

  // 직원 데이터 로드
  const loadEmployees = async () => {
    try {
      const employeeData = await employeeApi.getActive(userRole);
      setEmployees(employeeData.map(emp => ({
        id: emp.id,
        name: emp.name,
        department: emp.department,
        position: emp.position
      })));
    } catch (error) {
      showToast('직원 데이터를 불러올 수 없습니다.', 'error');
    }
  };

  // PPE 데이터를 가져와서 스케줄 이벤트로 변환
  const loadPPEData = async () => {
    try {
      setLoading(true);

      // PROJECT_MANAGER 권한인 경우 자신이 PM인 프로젝트만 조회
      let projects: any[];
      if (userRole === 'PROJECT_MANAGER') {
        projects = await projectApi.getMyProjects();
      } else {
        projects = await projectApi.getAll();
      }

      // 프로젝트 목록 저장
      setAllProjects(projects);

      const scheduleEvents: ScheduleEvent[] = [];
      const uniquePaymentTypes = new Set<string>(); // 입금 유형 수집

      // PPE 데이터 가져오기
      const ppeData = await ppeApi.getAll();
      const ppeMap = new Map(ppeData.map((ppe: any) => [ppe.project?.id, ppe]));

      // 관리자/개발자는 프로젝트 리스트에서 직접 이벤트 생성
      const projectsToProcess = userRole === 'PROJECT_MANAGER'
        ? projects.filter((p: any) => ppeData.some((ppe: any) => ppe.project?.id === p.id))
        : projects;

      projectsToProcess.forEach((project: any) => {
        if (project.startDate && project.endDate) {
          const ppe = ppeMap.get(project.id);

          // 참여자 정보 수집
          const participants = ppe?.manpowerItems && Array.isArray(ppe.manpowerItems)
            ? ppe.manpowerItems.filter((member: any) => member.name).map((member: any) => {
                const employee = employees.find(emp => emp.name === member.name);
                return {
                  name: member.name,
                  position: member.position || employee?.position || '',
                  department: employee?.department || '',
                  mm: member.mm || 0,
                  amount: member.amount || 0,
                  employeeId: employee?.id || null
                };
              })
            : [];

          // 프로젝트 이벤트 추가
          scheduleEvents.push({
            id: `project-${project.id}`,
            title: project.name || '프로젝트',
            start: project.startDate,
            end: project.endDate,
            type: 'PROJECT',
            employeeId: 0,
            employeeName: '프로젝트 팀',
            description: JSON.stringify({
              revenue: ppe?.revenue || 0,
              directCost: ppe?.directCost || 0,
              manpowerCost: ppe?.manpowerCost || 0,
              participants: participants,
              projectId: project.id
            }),
            location: ''
          });

          // 입금 예정일 이벤트 추가 (revenueItems에서)
          if (ppe?.revenueItems && Array.isArray(ppe.revenueItems)) {
            ppe.revenueItems.forEach((item: any, index: number) => {
              if (item.dueDate && item.name) {
                // 입금 유형 수집
                uniquePaymentTypes.add(item.name);

                scheduleEvents.push({
                  id: `payment-${project.id}-${index}`,
                  title: `${project.name} ${item.name}`,
                  start: item.dueDate,
                  end: item.dueDate,
                  type: 'PAYMENT',
                  paymentType: item.name,
                  employeeId: 0,
                  employeeName: '',
                  amount: item.amount || 0,
                  description: JSON.stringify({
                    projectId: project.id,
                    projectName: project.name,
                    paymentType: item.name,
                    amount: item.amount || 0,
                    dueDate: item.dueDate
                  })
                });
              }
            });
          }
        }
      });

      // 입금 유형 목록 설정 (정렬)
      const sortedTypes = Array.from(uniquePaymentTypes).sort();
      setPaymentTypes(sortedTypes);

      // 처음 로드시 모든 입금 유형 선택
      if (selectedPaymentTypes.size === 0 && sortedTypes.length > 0) {
        setSelectedPaymentTypes(new Set(sortedTypes));
      }

      setEvents(scheduleEvents);
    } catch (error) {
      showToast('PPE 데이터를 불러올 수 없습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  // userRole과 employees가 로드되면 PPE 데이터 로드
  useEffect(() => {
    if (userRole && employees.length > 0) {
      loadPPEData();
    }
  }, [userRole, employees]);

  const openPPEDetail = (event: ScheduleEvent) => {
    // 프로젝트 타입인 경우 PPE 상세 모달 열기
    if (event.type === 'PROJECT' && event.description) {
      try {
        const projectData = JSON.parse(event.description);
        const projectId = projectData.projectId;

        // allProjects에서 해당 프로젝트 찾기
        const project = allProjects.find(p => p.id === projectId);
        if (project) {
          setSelectedPPEProject(project);
          setIsPPEDetailOpen(true);
        }
      } catch (e) {
        showToast('프로젝트 정보 파싱에 실패했습니다.', 'error');
      }
    }
  };

  const closePPEDetailModal = () => {
    setIsPPEDetailOpen(false);
    setSelectedPPEProject(undefined);
  };


  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long'
    });
  };

  // 달력 생성 함수들
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);
    
    const days: (number | null)[] = [];
    
    // 이전 달의 빈 칸들
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getEventsForDay = (day: number) => {
    const targetYear = currentDate.getFullYear();
    const targetMonth = currentDate.getMonth();
    const filteredEvents = getFilteredEvents();

    return filteredEvents.filter(event => {
      // 날짜 문자열에서 년/월/일 추출 (타임존 문제 방지)
      const [startYear, startMonth, startDay] = event.start.split('-').map(Number);
      const [endYear, endMonth, endDay] = event.end.split('-').map(Number);

      const targetDateNum = targetYear * 10000 + (targetMonth + 1) * 100 + day;
      const startDateNum = startYear * 10000 + startMonth * 100 + startDay;
      const endDateNum = endYear * 10000 + endMonth * 100 + endDay;

      // 해당 날짜가 이벤트 기간에 포함되는지 확인
      return targetDateNum >= startDateNum && targetDateNum <= endDateNum;
    });
  };

  const isToday = (day: number) => {
    const today = new Date();
    return today.getFullYear() === currentDate.getFullYear() &&
           today.getMonth() === currentDate.getMonth() &&
           today.getDate() === day;
  };

  const getEventTypeColor = (type: string) => {
    const colors = {
      'MEETING': 'bg-blue-100 text-blue-800 border-blue-200',
      'PROJECT': 'bg-green-100 text-green-800 border-green-200',
      'LEAVE': 'bg-red-100 text-red-800 border-red-200',
      'TRAINING': 'bg-purple-100 text-purple-800 border-purple-200',
      'PAYMENT': 'bg-amber-100 text-amber-800 border-amber-300',
      'OTHER': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[type as keyof typeof colors] || colors.OTHER;
  };

  // 입금 유형 체크박스 토글
  const togglePaymentType = (type: string) => {
    setSelectedPaymentTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  // 필터링된 이벤트 반환
  const getFilteredEvents = () => {
    return events.filter(event => {
      if (event.type === 'PROJECT') {
        return showProjects;
      }
      if (event.type === 'PAYMENT') {
        // paymentTypes가 로드되기 전이면 모두 표시, 로드 후엔 선택된 것만 표시
        if (paymentTypes.length === 0) return true;
        return event.paymentType && selectedPaymentTypes.has(event.paymentType);
      }
      return true;
    });
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-slate-600">PPE 데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Calendar</h1>
            <p className="text-slate-500 mt-1">PPE 프로젝트 일정 관리 및 확인</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={async () => {
                await loadEmployees();
                await loadPPEData();
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
            >
              새로고침
            </button>
          </div>
        </div>

        {/* 필터 체크박스 */}
        <div className="flex flex-wrap items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <span className="text-sm font-medium text-slate-700">표시:</span>

          {/* 프로젝트 필터 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showProjects}
              onChange={(e) => setShowProjects(e.target.checked)}
              className="w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500"
            />
            <span className="text-sm text-slate-700 flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-green-200 border border-green-300"></span>
              프로젝트
            </span>
          </label>

          {/* 구분선 */}
          {paymentTypes.length > 0 && (
            <span className="text-slate-300">|</span>
          )}

          {/* 입금 유형 필터 (데이터에서 동적으로) */}
          {paymentTypes.map(type => (
            <label key={type} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPaymentTypes.has(type)}
                onChange={() => togglePaymentType(type)}
                className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
              />
              <span className="text-sm text-slate-700 flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-amber-200 border border-amber-300"></span>
                {type}
              </span>
            </label>
          ))}
        </div>
      </header>

      {/* 월 네비게이션 */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="text-xl font-bold text-slate-800">
          {formatDate(currentDate)}
        </div>
        <button
          onClick={() => navigateMonth('next')}
          className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 달력 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
            <div key={day} className={`p-4 text-center font-semibold text-sm ${
              index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-slate-700'
            }`}>
              {day}
            </div>
          ))}
        </div>

        {/* 달력 날짜들 */}
        <div className="grid grid-cols-7">
          {generateCalendarDays().map((day, index) => (
            <div key={index} className="min-h-[160px] border-r border-b border-slate-100 last:border-r-0">
              {day && (
                <div className="p-2 h-full">
                  <div className={`flex items-center justify-center w-8 h-8 text-sm font-medium mb-2 ${
                    isToday(day) 
                      ? 'bg-indigo-600 text-white rounded-full' 
                      : index % 7 === 0 
                        ? 'text-red-600' 
                        : index % 7 === 6 
                          ? 'text-blue-600' 
                          : 'text-slate-700'
                  }`}>
                    {day}
                  </div>
                  
                  {/* 해당 날짜의 일정들 */}
                  <div className="space-y-1 overflow-y-auto max-h-24">
                    {getEventsForDay(day).map((event) => {
                      let eventColor = getEventTypeColor(event.type);

                      // 프로젝트인 경우 프로젝트별 색상 적용
                      if (event.type === 'PROJECT' && event.description) {
                        try {
                          const projectData = JSON.parse(event.description);
                          eventColor = getProjectColor(projectData.projectId);
                        } catch (e) {
                          // JSON 파싱 실패시 기본 색상 사용
                        }
                      }

                      // 입금 이벤트: 금액 포함 표시
                      const displayText = event.type === 'PAYMENT' && event.amount
                        ? `${event.title} (${event.amount.toLocaleString()}만원)`
                        : event.title;

                      return (
                        <div
                          key={event.id}
                          onClick={() => event.type === 'PROJECT' ? openPPEDetail(event) : undefined}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltipInfo({ text: displayText, x: rect.left, y: rect.top - 30 });
                          }}
                          onMouseLeave={() => setTooltipInfo(null)}
                          className={`text-xs p-1 rounded truncate ${eventColor} ${
                            event.type === 'PROJECT' ? 'cursor-pointer' : ''
                          } ${event.type === 'PAYMENT' ? 'font-medium border' : ''}`}
                        >
                          {event.type === 'PAYMENT' && (
                            <span className="mr-1 font-bold">₩</span>
                          )}
                          {event.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <NewProjectModal
        isOpen={isPPEDetailOpen}
        onClose={closePPEDetailModal}
        project={selectedPPEProject}
        readOnly={true}
        onSave={async (updatedData: any) => {
          // PPE 데이터 업데이트 후 새로고침
          await loadEmployees();
          await loadPPEData();
          closePPEDetailModal();
        }}
      />

      {/* 즉시 표시 툴팁 */}
      {tooltipInfo && (
        <div
          className="fixed z-[9999] bg-slate-800 text-white text-xs rounded px-2 py-1 shadow-lg pointer-events-none whitespace-nowrap"
          style={{ left: tooltipInfo.x, top: tooltipInfo.y }}
        >
          {tooltipInfo.text}
        </div>
      )}
    </div>
  );
};

export default ScheduleAdmin;